/**
 * Tools → Features: the whole sections of Roof Board that are switched off.
 *
 * Not a preferences screen. Everything here is a part of the app that was
 * built, works, and is not being used — hidden rather than deleted, because
 * deleting it is a rebuild the day somebody wants it back and hiding it is a
 * tick in a box (ADR 0144).
 *
 * Admin only: turning a section on for one person turns it on for everybody,
 * which is not a rep's call.
 */

import { useCallback, useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { supabase } from "../../lib/supabase";
import { useSession } from "../../session";

/**
 * One switch. Adding another is a row here and a column on `company_settings`
 * — the screen itself does not grow.
 */
interface Feature {
  /** The `company_settings` column that holds it. */
  column: "partners_enabled" | "quote_check_enabled";
  /** What the checkbox says. */
  label: string;
  /** What the section is, in the words of somebody who does not build it. */
  blurb: string;
  /** What it says after it is ticked, and after it is unticked. */
  onNote: string;
  offNote: string;
}

const FEATURES: Feature[] = [
  {
    column: "partners_enabled",
    label: "Referral partners: texts, referrals and storm alerts",
    blurb:
      "The Partners tab is always there as the business card file: scan a card, and see every card and what line of work its owner is in. This switch adds the rest of the partner network on top — a monthly text from the rep’s own phone, a text after a storm, a Partners card on a job, and a record of which referral turned into a roof. Nothing has been deleted, and turning it off deletes nothing.",
    onNote:
      "Partners shows the whole network again: texts, referrals and storms. Reload to see it.",
    offNote:
      "Partners is back to the business card file alone. Nothing was deleted. Reload to see it.",
  },
  {
    column: "quote_check_enabled",
    label: "Quote check on a material order",
    blurb:
      "The panel at the top of a material order that compares it line by line with the estimate it came from, lists every difference with the quoted number beside the ordered one, and offers to put each one back. Off since 2026-09-21: it works, but on a real order it listed fourteen differences of which only a couple were mistakes, and a warning that is usually wrong is a warning people scroll past. While it is off, an order is not checked, nothing is asked of Jev, and placing an order says nothing about the quote. Nothing has been deleted — ticking this puts the panel back, and every review already recorded is still there.",
    onNote:
      "Material orders are checked against their quote again. Reopen an order to see it.",
    offNote:
      "The quote panel is gone from every material order. Nothing was deleted. Reopen an order to see it.",
  },
];

export function FeaturesTool() {
  const { isAdmin } = useSession();

  const [on, setOn] = useState<Partial<Record<Feature["column"], boolean>>>({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      /*
       * `select *`, not the columns by name. A database this branch's
       * migration has not reached yet would refuse a select naming a column it
       * does not have, and that would take the whole screen down rather than
       * one checkbox — the failure of 2026-08-18 in miniature. Absent reads as
       * off, which is what both switches mean by default anyway.
       */
      const { data, error: loadError } = await supabase
        .from("company_settings")
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      if (!alive) return;
      if (loadError) {
        setError(loadError.message);
        return;
      }
      const row = (data ?? {}) as Partial<Record<Feature["column"], unknown>>;
      setOn(
        Object.fromEntries(
          FEATURES.map((f) => [f.column, row[f.column] === true]),
        ),
      );
      setLoaded(true);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const save = useCallback(async (feature: Feature, next: boolean) => {
    setBusy(true);
    setError(null);
    setNote(null);
    const { error: saveError } = await supabase
      .from("company_settings")
      .update({ [feature.column]: next })
      .eq("id", 1);
    setBusy(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setOn((was) => ({ ...was, [feature.column]: next }));
    setNote(next ? t(feature.onNote) : t(feature.offNote));
  }, []);

  if (!isAdmin) {
    return (
      <div className="muted">{t("Only an admin can switch a section on.")}</div>
    );
  }

  return (
    <div className="block">
      {error && <ErrorNote error={error} />}
      {note && <div className="muted">{note}</div>}

      {FEATURES.map((feature) => (
        <div key={feature.column}>
          <div className="row">
            <label>
              <input
                type="checkbox"
                disabled={busy || !loaded}
                checked={on[feature.column] === true}
                onChange={(e) => void save(feature, e.target.checked)}
              />{" "}
              {t(feature.label)}
            </label>
          </div>
          <div className="muted">{t(feature.blurb)}</div>
        </div>
      ))}
    </div>
  );
}
